<template>
	<el-dialog
		title="选择商品"
		v-model="innerVisible"
		width="860px"
		top="6vh"
		:close-on-click-modal="false"
		@open="onOpen"
	>
		<div class="product-selector">
			<div class="toolbar">
				<el-input
					v-model="query.keyword"
					placeholder="搜索商品名称"
					clearable
					style="width: 240px; margin-right: 10px;"
					@keyup.enter="onSearch"
				>
					<template #prefix>
						<el-icon><Search /></el-icon>
					</template>
				</el-input>
				<el-select
					v-model="query.categoryId"
					placeholder="全部分类"
					clearable
					style="width: 160px; margin-right: 10px;"
					@change="onSearch"
				>
					<el-option
						v-for="c in categoryList"
						:key="c.id"
						:label="c.name"
						:value="c.id"
					></el-option>
				</el-select>
				<el-button type="primary" @click="onSearch">查询</el-button>
				<el-button @click="onReset">重置</el-button>
				<span class="picked-tip">已选 {{ selectedRows.length }} 项</span>
			</div>

			<el-table
				ref="tableRef"
				:data="tableData"
				v-loading="loading"
				:header-cell-style="{ background: '#f7f7f7', color: '#333' }"
				row-key="id"
				@selection-change="onSelectionChange"
				@row-click="onRowClick"
				highlight-current-row
				style="width: 100%;"
				height="420"
			>
				<el-table-column type="selection" width="44" reserve-selection></el-table-column>
				<el-table-column align="center" label="封面" width="80">
					<template #default="{ row }">
						<img
							v-if="row.coverImage"
							:src="resolveImg(row.coverImage)"
							style="width: 48px; height: 48px; object-fit: cover; border-radius: 4px;"
						/>
						<span v-else>-</span>
					</template>
				</el-table-column>
				<el-table-column label="商品名称" prop="name" min-width="200" show-overflow-tooltip></el-table-column>
				<el-table-column label="副标题" prop="subtitle" min-width="160" show-overflow-tooltip></el-table-column>
				<el-table-column align="right" label="零售价" width="120">
					<template #default="{ row }">¥{{ formatPrice(row.retailPrice) }}</template>
				</el-table-column>
				<el-table-column align="center" label="状态" width="80">
					<template #default="{ row }">
						<span :style="{ color: row.status === 1 ? '#67c23a' : '#f56c6c' }">
							{{ row.status === 1 ? '上架' : '下架' }}
						</span>
					</template>
				</el-table-column>
			</el-table>

			<div class="pagination-wrap">
				<el-pagination
					layout="prev, pager, next, jumper"
					:current-page="query.pageNum"
					:page-size="query.pageSize"
					:total="total"
					@current-change="onPageChange"
				></el-pagination>
			</div>
		</div>
		<template #footer>
			<el-button @click="onCancel">取消</el-button>
			<el-button type="primary" @click="onConfirm" :disabled="selectedRows.length === 0">
				确定（{{ selectedRows.length }}）
			</el-button>
		</template>
	</el-dialog>
</template>

<script>
import { Search } from '@element-plus/icons-vue';
import axiosconf from '../../api/axiosconf';
import globle from '../../globle/globle';

export default {
	name: 'ProductSelectorDialog',
	components: { Search },
	props: {
		modelValue: { type: Boolean, default: false },
		// 已选 ID 列表（用于打开时回选）
		selectedIds: { type: Array, default: () => [] },
	},
	emits: ['update:modelValue', 'confirm'],
	data() {
		return {
			GLOBAL: globle,
			loading: false,
			tableData: [],
			total: 0,
			categoryList: [],
			query: {
				pageNum: 1,
				pageSize: 10,
				keyword: '',
				categoryId: null,
				status: 1,
			},
			selectedRows: [],
		};
	},
	computed: {
		innerVisible: {
			get() { return this.modelValue; },
			set(val) { this.$emit('update:modelValue', val); },
		},
	},
	methods: {
		onOpen() {
			this.query.pageNum = 1;
			this.query.keyword = '';
			this.query.categoryId = null;
			this.selectedRows = [];
			this.fetchCategories();
			this.fetchList().then(() => this.restoreSelection());
		},
		async fetchList() {
			this.loading = true;
			try {
				const res = await axiosconf.applicationJson('/mall/admin/product/list', {
					pageNum: this.query.pageNum,
					pageSize: this.query.pageSize,
					keyword: this.query.keyword || undefined,
					categoryId: this.query.categoryId || undefined,
					status: this.query.status,
				});
				if (res.data && res.data.code === 0) {
					const page = res.data.data || {};
					this.tableData = page.records || [];
					this.total = page.total || 0;
				}
			} catch (e) {
				this.$message.error('获取商品列表失败');
			} finally {
				this.loading = false;
			}
			this.$nextTick(() => this.restoreSelection());
		},
		async fetchCategories() {
			try {
				const res = await axiosconf.applicationJson('/mall/admin/product/category/list');
				if (res.data && res.data.code === 0) {
					this.categoryList = res.data.data || [];
				}
			} catch (e) {
				// 静默失败，分类筛选只是辅助
			}
		},
		// 翻页/搜索后，根据 selectedRows + selectedIds 把已选行勾上
		// 注意：toggleRowSelection 不会主动触发 selection-change，所以同步维护 selectedRows
		restoreSelection() {
			const tableRef = this.$refs.tableRef;
			if (!tableRef) return;
			const pickedIds = new Set(this.selectedRows.map((r) => r.id));
			(this.selectedIds || []).forEach((id) => pickedIds.add(id));
			const idToRow = new Map(this.selectedRows.map((r) => [r.id, r]));
			this.tableData.forEach((row) => {
				if (pickedIds.has(row.id)) {
					tableRef.toggleRowSelection(row, true);
					idToRow.set(row.id, row);
				}
			});
			this.selectedRows = Array.from(idToRow.values());
		},
		onSelectionChange(rows) {
			this.selectedRows = rows;
		},
		onRowClick(row) {
			const tableRef = this.$refs.tableRef;
			if (!tableRef) return;
			tableRef.toggleRowSelection(row);
		},
		onSearch() {
			this.query.pageNum = 1;
			this.fetchList();
		},
		onReset() {
			this.query.keyword = '';
			this.query.categoryId = null;
			this.query.pageNum = 1;
			this.fetchList();
		},
		onPageChange(page) {
			this.query.pageNum = page;
			this.fetchList();
		},
		onCancel() {
			this.innerVisible = false;
		},
		onConfirm() {
			// 仅取展示需要的 4 字段（与后端 MallProductBriefDTO 对齐）
			const payload = this.selectedRows.map((r) => ({
				id: r.id,
				name: r.name,
				coverImage: r.coverImage,
				retailPrice: r.retailPrice,
			}));
			this.$emit('confirm', payload);
			this.innerVisible = false;
		},
		formatPrice(val) {
			if (val === null || val === undefined) return '0.00';
			return parseFloat(val).toFixed(2);
		},
		resolveImg(url) {
			if (!url) return '';
			if (/^https?:\/\//i.test(url) || url.startsWith('data:')) return url;
			const prefix = (this.GLOBAL && this.GLOBAL.imgPath) || '';
			return prefix + url;
		},
	},
};
</script>

<style lang="less" scoped>
.product-selector {
	.toolbar {
		display: flex;
		align-items: center;
		margin-bottom: 12px;
		.picked-tip {
			margin-left: auto;
			color: #679ff6;
			font-size: 13px;
		}
	}
	.pagination-wrap {
		margin-top: 12px;
		display: flex;
		justify-content: flex-end;
	}
}
</style>
